import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

/** Below this % of bar width, legend label is nudged left so it stays readable. */
const NARROW_LEGEND_SEGMENT_PERCENT = 16;

/**
 * Intensity strip (%1RM buckets). Segment width ∝ count vs total; zones with count 0 are omitted
 * from the bar and legend so narrow columns do not break label layout.
 */
function InsightsIntensityZonesBar({ zones }) {
  const { t } = useTranslation("athlete-portal");
  const labelKeyPrefix = "programs.programInsights.panel.intensityZones";

  const visibleZones = useMemo(
    () => zones.filter((z) => z.count > 0),
    [zones]
  );

  /** Bar and legend share the same column % so each label sits under its segment. */
  const { segmentPercentsBar, gridTemplateColumns } = useMemo(() => {
    const total = visibleZones.reduce((acc, z) => acc + z.count, 0);
    if (total > 0) {
      const pcts = visibleZones.map((z) => (z.count / total) * 100);
      return {
        segmentPercentsBar: pcts,
        gridTemplateColumns: pcts.map((p) => `${p}%`).join(" "),
      };
    }
    return { segmentPercentsBar: [], gridTemplateColumns: "" };
  }, [visibleZones]);

  return (
    <div className="ProgramInsights-intensityZonesBar">
      <div className="ProgramInsights-intensityZonesTrack">
        {visibleZones.map((z, i) => (
          <div
            key={z.zoneId}
            className={`ProgramInsights-intensityZoneSeg ProgramInsights-intensityZoneSeg--${z.zoneId}`}
            style={{ width: `${segmentPercentsBar[i]}%` }}
          />
        ))}
      </div>
      {visibleZones.length > 0 ? (
        <div
          className="ProgramInsights-intensityZonesLegend"
          style={{ gridTemplateColumns }}
        >
          {visibleZones.map((z, i) => {
            const label = t(`${labelKeyPrefix}.${z.zoneId}`, { count: z.count });
            const isNarrow =
              segmentPercentsBar[i] < NARROW_LEGEND_SEGMENT_PERCENT;
            return (
              <span
                key={z.zoneId}
                className={`ProgramInsights-intensityZonesLegendItem ProgramInsights-intensityZonesLegendItem--${z.zoneId}${
                  isNarrow
                    ? " ProgramInsights-intensityZonesLegendItem--narrow"
                    : ""
                }`}
              >
                {label}
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

InsightsIntensityZonesBar.propTypes = {
  zones: PropTypes.arrayOf(
    PropTypes.shape({
      zoneId: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ).isRequired,
};

export default memo(InsightsIntensityZonesBar);
